import { connect } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import Moment from 'react-moment';
import { useBoolean } from "@fluentui/react-hooks";

import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel
} from "@fluentui/react-components";
import {
    DocumentBulletListRegular
} from "@fluentui/react-icons";

// functions
import * as ComponentFunctions from './PractitionerProtocolVisit.List.Functions';

// styles
import './PractitionerProtocolVisit.List.Styles.css';

// models
import { Protocol } from "../../../models/Entities/Protocols/Protocol";
import { PractitionerVisit } from "../../../models/Entities/Practitioners/Practitioner";
import { PatientVisit } from "../../../models/Entities/Protocols/PatientVisist";

// children
import ProtocolHeaderComponent from "../../Protocols/ProtocolHeader/ProtocolHeader.Component";
import SettlementFormComponent from "../../Settlements/SettlementForm/Settlement.Form.Component";


const PractitionerProtocolVisitList = (props: ComponentFunctions.Props) => {

    const [isOpenSettlementFormPanel, { setFalse: dismissSettlementFormPanel }] =
        useBoolean(false);

    const [selectedProtocol, setSelectedProtocol] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (props.PractitionerId) {
            props.GetPractitionerById(props.PractitionerId);
        }
    }, [props.PractitionerId]);

    const practitionerReady = props.Practitioner !== undefined;
    useEffect(() => {
        if (practitionerReady) {
            props.GetProtocolsByPractitionerId(props.PractitionerId);
        }
    }, [practitionerReady, props.PractitionerId]);

    // Memoized filtered protocols
    const filteredProtocols = useMemo<Protocol[]>(() => {
        if (!props.Protocols) return [];
        if (props.protocolsIds?.length > 0) {
            const idSet = new Set(props.protocolsIds);
            return props.Protocols.filter((p: Protocol) => idSet.has(p.id));
        }
        return props.Protocols;
    }, [props.Protocols, props.protocolsIds]);

    const protocolIdsKey = useMemo(
        () => filteredProtocols.map((p) => p.id).sort((a, b) => a - b).join(','),
        [filteredProtocols]
    );

    // Trigger fetch only when ids or range really change
    useEffect(() => {
        if (!protocolIdsKey || !props.PractitionerId) return;
        protocolIdsKey.split(',').map(Number).forEach((id) => {
            props.GetPractitionerProtocolVisits(props.PractitionerId, id, props.DateFrom, props.DateTo);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [protocolIdsKey, props.PractitionerId, props.DateFrom, props.DateTo]);

    // Pre-index visits by protocol id (avoids repeated Array.find on every render)
    const visitsByProtocolId = useMemo<Map<number, PatientVisit[]>>(() => {
        const map = new Map<number, PatientVisit[]>();
        props.visitis?.forEach((pv: PractitionerVisit) => {
            map.set(pv.protocolId, pv.visits ?? []);
        });
        return map;
    }, [props.visitis]);

    return (
        <div className="visitList-container">
            {filteredProtocols.length === 0 ? (
                <div className="visitList-empty">Sin protocolos para mostrar.</div>
            ) : (
                <Accordion collapsible>
                    {filteredProtocols.map((protocol: Protocol) => {
                        const visits = visitsByProtocolId.get(protocol.id) ?? [];
                        return (
                            <AccordionItem key={protocol.id} value={protocol.id} className="visitList-accordionItem">
                                <AccordionHeader
                                    expandIconPosition="end"
                                    onClick={() => setSelectedProtocol(protocol.id)}
                                    icon={<DocumentBulletListRegular />}
                                >
                                    <span className="visitList-accordionHeader">
                                        <span className="visitList-headerTitle">{protocol.title}</span>
                                        <span className="visitList-headerCount" title="Cantidad de visitas">
                                            <span className="visitList-headerCount-value">{visits.length}</span>
                                            <span className="visitList-headerCount-label">visitas</span>
                                        </span>
                                    </span>
                                </AccordionHeader>
                                <AccordionPanel>
                                    <div className="visitList-panelBody">
                                        <ProtocolHeaderComponent ProtocolId={protocol.id} />

                                        {visits.length === 0 ? (
                                            <div className="visitList-empty">Sin visitas registradas en el período.</div>
                                        ) : (
                                            <div className="visitList-tableWrap">
                                                <table className="visitList-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="col-date">Fecha</th>
                                                            <th>Visita</th>
                                                            <th className="col-mod">Modalidad</th>
                                                            <th className="col-tipo">Tipo</th>
                                                            <th className="col-estado">Estado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {visits.map((visit: PatientVisit, idx: number) => (
                                                            <tr key={`${protocol.id}-${visit.date}-${visit.numberInProtocol}-${idx}`}>
                                                                <td className="col-date">
                                                                    <div className="visitDate">
                                                                        <span className="visitDate-primary">
                                                                            <Moment date={visit.date} format="DD MMM" />
                                                                        </span>
                                                                        <span className="visitDate-secondary">
                                                                            <Moment date={visit.date} format="YYYY" />
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="visitTitle">
                                                                        <span className="visitTitle-num">#{visit.numberInProtocol}</span>
                                                                        <span className="visitTitle-text">{visit.title}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="col-mod">
                                                                    <span className={`visitType ${visit.visitType === 0 ? 'presencial' : 'remoto'}`}>
                                                                        {visit.visitType === 0 ? 'Presencial' : 'Remoto'}
                                                                    </span>
                                                                </td>
                                                                <td className="col-tipo visitTypeText">{visit.type}</td>
                                                                <td className="col-estado">
                                                                    <span className={`visitStatus ${ComponentFunctions.GetStatusClass(visit.status)}`}>
                                                                        {ComponentFunctions.GetStatusDescription(visit.status)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </AccordionPanel>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            )}

            <SettlementFormComponent
                isOpen={isOpenSettlementFormPanel}
                openPanel={dismissSettlementFormPanel}
                dismissPanel={dismissSettlementFormPanel}
                ProtocolId={selectedProtocol}
                PractitionerId={props.PractitionerId}
            />
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PractitionerProtocolVisitList as any);
